import { Row } from 'react-bootstrap';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import Button from 'components/button/Button';
import useFetchAgencyChannelSettings from 'components/domain/channelSettings/useFetchAgencyChannelSettings';
import LeadChannelIcon from 'components/domain/lead/LeadChannelIcon';
import RadioGroupField from 'components/forms/radioGroupField/RadioGroupField';
import SelectField from 'components/forms/selectField/SelectField';
import TextField from 'components/forms/textField/TextField';
import { AirbnbCancellationPolicy } from 'models/AirbnbCancellationPolicy';
import { AirbnbCheckInOptions } from 'models/AirbnbCheckInOptions';
import { Channel } from 'models/Channels';
import { LeadChannel } from 'models/Leads';
import { PropertyBusinessType } from 'models/Properties';
import { PropertyListingTypes } from 'models/PropertyListingTypes';
import {
  FormSectionColumn,
  SectionTitleContainer,
} from 'pages/property/common/Common.styles';
import PropertyFormFieldLabel from 'pages/property/common/PropertyFormFieldLabel';
import TooltipContentWithLearnMore from 'pages/property/common/tooltip/TooltipContentWithLearnMore';
import usePropertySettingsInitData from 'pages/property/usePropertySettingsInitData';
import { PropertyDetailsCollapseStyled } from '../propertyDetails/PropertyDetails.styles';
import { PropertySettingsMainTabFormValues } from '../../PropertySettingsMainTab.schema';
import {
  AirbnbPromotion,
  AirbnbPromotionStatus,
} from '../../PropertySettingsMainTab.types';
import {
  NonRefundableRateDiscountContainer,
  OpenSetInstructionsContainer,
  ViewAirbnbResolutionsStyled,
} from './AirbnbSettings.styles';
import NonRefundableRateDiscountField from './NonRefundableRateDiscountField';
import AllowBookingRequestField from './AllowBookingRequestField';
import SendMessageAsField from './SendMessageAsField';
import GuestRequirementsField from './GuestRequirementsField';
import BookingTypeField from './BookingTypeField';
import { checkGuestRequirementsFieldVisibility } from './AirbnbSettings.utils';
import CheckInTimeEndField from './CheckInTimeEndField';
import CheckInTimeStartField from './CheckInTimeStartField';
import useSetInstructionsModal from './useSetInstructionsModal';

const AirbnbSettings = () => {
  const { t } = useTranslation();
  const { getCopyAddonIcon } = useCopyToClipboard();
  const { property } = usePropertySettingsInitData();
  const { openSetInstructionsModal } = useSetInstructionsModal();
  const {
    // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    airbnbSettings: { airbnbId, promotions },
  } = useWatch<PropertySettingsMainTabFormValues>();

  const { channelSettings, isFetching: isLoadingChannelSettings } =
    useFetchAgencyChannelSettings(true);

  const isAirbnbActive = !!channelSettings?.find(
    ({ enumId }) => enumId === Channel.airbnb,
  )?.isActive;

  const isHotel = property?.businessType === PropertyBusinessType.HOTEL;

  if (!isAirbnbActive || isLoadingChannelSettings || isHotel) {
    return null;
  }

  const listingTypeOptions = [
    PropertyListingTypes.ENTIREHOME,
    PropertyListingTypes.SHAREDROOM,
    PropertyListingTypes.PRIVATEROOM,
  ].map((value) => ({
    label: t(
      `pageProperty.mainSettings.airbnbSettings.listingTypeOptions.${value}`,
    ),
    value,
  }));

  const cancellationPolicyOptions = Object.values(AirbnbCancellationPolicy).map(
    (value) => ({
      label: t(
        `pageProperty.mainSettings.airbnbSettings.cancellationPolicyOptions.${value}`,
      ),
      value,
    }),
  );

  const checkInOptions = Object.values(AirbnbCheckInOptions).map((value) => ({
    label: t(
      `pageProperty.mainSettings.airbnbSettings.checkInOptionOptions.${value}`,
    ),
    value,
  }));

  const nonRefundableRateOptions = [
    { label: t('common.disabled'), value: 'false' },
    { label: t('common.enabled'), value: 'true' },
  ];

  const promotionOptions = [
    { label: t('common.disabled'), value: AirbnbPromotionStatus.AVAILABLE },
    { label: t('common.enabled'), value: AirbnbPromotionStatus.ONGOING },
  ];

  const isStandaloneProperty =
    property?.businessType === PropertyBusinessType.STANDALONE_PROPERTY;
  const isSubUnit = property?.businessType === PropertyBusinessType.SUB_UNIT;
  const isGuestRequirementsFieldVisible =
    checkGuestRequirementsFieldVisibility(property);
  const isCancellationPolicyFieldVisible = isStandaloneProperty || isSubUnit;
  const isCheckInOptionFieldVisible = isStandaloneProperty || isSubUnit;
  const isNonRefundableRateFieldVisible = isStandaloneProperty || isSubUnit;
  const isViewAirbnbResolutionsVisible = isStandaloneProperty || isSubUnit;
  const isPromotionFieldVisible =
    promotions?.filter(
      (p: AirbnbPromotion) => p.status !== AirbnbPromotionStatus.EXPIRED,
    ).length > 0;

  const sectionTitle = (
    <SectionTitleContainer>
      <LeadChannelIcon channel={LeadChannel.AIRBNB} />
      {t('pageProperty.mainSettings.airbnbSettings.sectionTitle')}
    </SectionTitleContainer>
  );

  return (
    <PropertyDetailsCollapseStyled
      data-testid="airbnb-settings"
      defaultExpanded
      label={sectionTitle}
      variant="card"
    >
      <fieldset>
        <Row>
          <FormSectionColumn>
            <TextField
              label={t('pageProperty.mainSettings.airbnbSettings.airbnbId')}
              name="airbnbSettings.airbnbId"
              colSmInput={7}
              colSmLabel={5}
              disabled
              rightAddons={[getCopyAddonIcon(airbnbId)]}
            />
            <SelectField
              label={t('pageProperty.mainSettings.airbnbSettings.listingType')}
              name="airbnbSettings.listingType"
              options={listingTypeOptions}
              colSmInput={7}
              colSmLabel={5}
              required
            />
            <BookingTypeField />
            <AllowBookingRequestField />
            {isGuestRequirementsFieldVisible && <GuestRequirementsField />}
            {isCancellationPolicyFieldVisible && (
              <SelectField
                label={
                  <PropertyFormFieldLabel name="airbnbSettings.cancellationPolicy">
                    {t(
                      'pageProperty.mainSettings.airbnbSettings.cancellationPolicy',
                    )}
                  </PropertyFormFieldLabel>
                }
                name="airbnbSettings.cancellationPolicy"
                options={cancellationPolicyOptions}
                colSmInput={7}
                colSmLabel={5}
                required
                rightTooltip={
                  <TooltipContentWithLearnMore
                    labelTooltipText={t(
                      'pageProperty.mainSettings.airbnbSettings.cancellationPolicyTooltip',
                    )}
                    learnMoreUrl="https://help.hostfully.com/en/articles/1598674-activate-airbnb-channel#h_e72e9e0c72"
                  />
                }
              />
            )}
          </FormSectionColumn>
          <FormSectionColumn>
            <SendMessageAsField />
            <CheckInTimeStartField />
            <CheckInTimeEndField />
            {isCheckInOptionFieldVisible && (
              <>
                <SelectField
                  label={t(
                    'pageProperty.mainSettings.airbnbSettings.checkInOption',
                  )}
                  name="airbnbSettings.checkInOption"
                  options={checkInOptions}
                  colSmInput={7}
                  colSmLabel={5}
                />
                <OpenSetInstructionsContainer sm={7} smOffset={5}>
                  <Button
                    bsStyle="link"
                    data-testid="open-set-instructions-modal"
                    onClick={openSetInstructionsModal}
                  >
                    {t(
                      'pageProperty.mainSettings.airbnbSettings.setInstructions',
                    )}
                  </Button>
                </OpenSetInstructionsContainer>
              </>
            )}
            {isNonRefundableRateFieldVisible && (
              <>
                <RadioGroupField
                  label={
                    <PropertyFormFieldLabel name="airbnbSettings.nonRefundableRateEnabled">
                      {t(
                        'pageProperty.mainSettings.airbnbSettings.nonRefundableRate',
                      )}
                    </PropertyFormFieldLabel>
                  }
                  name="airbnbSettings.nonRefundableRateEnabled"
                  options={nonRefundableRateOptions}
                  colSmInput={7}
                  colSmLabel={5}
                  horizontal
                />
                <NonRefundableRateDiscountContainer
                  xs={8}
                  xsOffset={4}
                  sm={7}
                  smOffset={5}
                >
                  <NonRefundableRateDiscountField />
                </NonRefundableRateDiscountContainer>
              </>
            )}

            {isPromotionFieldVisible && (
              <RadioGroupField
                label={
                  <PropertyFormFieldLabel name="airbnbSettings.promotions[0].status">
                    {t('pageProperty.mainSettings.airbnbSettings.promotions')}
                  </PropertyFormFieldLabel>
                }
                name="airbnbSettings.promotions[0].status"
                options={promotionOptions}
                colSmInput={7}
                colSmLabel={5}
                horizontal
                rightTooltip={
                  <TooltipContentWithLearnMore
                    labelTooltipText={t(
                      'pageProperty.mainSettings.airbnbSettings.promotionsTooltip',
                    )}
                    learnMoreUrl="https://help.hostfully.com/en/articles/9414601-send-special-offers-or-give-discounts-promotions-to-airbnb-guests#h_1a13b6bde6"
                  />
                }
              />
            )}

            {isViewAirbnbResolutionsVisible && (
              <ViewAirbnbResolutionsStyled
                data-testid="view-airbnb-resolutions"
                to="/channels/airbnb/resolutions"
                target="_blank"
                rel="noreferrer"
              >
                {t(
                  'pageProperty.mainSettings.airbnbSettings.viewAirbnbResolutions',
                )}
              </ViewAirbnbResolutionsStyled>
            )}
          </FormSectionColumn>
        </Row>
      </fieldset>
    </PropertyDetailsCollapseStyled>
  );
};

export default AirbnbSettings;
